import _ from 'lodash';
import moment from 'moment';
import DepsService from 'services/dependencies';
import helpers from 'auth/helpers/menu';
import { RETAILER_TYPE, CHAIN_SUFFIX } from 'app/validators/roles';

import menu from 'app/config/sidemenu';

const namespace = 'session';
export const SET_PAGE_TITLE = `${namespace}_SET_PAGE_TITLE`;
export const SET_LOCALE = `${namespace}_SET_LOCALE`;
export const INIT_SESSION = `${namespace}_INIT_SESSION`;
export const UPDATE_SESSION = `${namespace}_UPDATE_SESSION`;
export const SET_SIDE_MENU = `${namespace}_SET_SIDE_MENU`;
export const SET_TOP_MENU = `${namespace}_SET_TOP_MENU`;
export const SET_ACTIVE_ITEM = `${namespace}_SET_ACTIVE_ITEM`;
export const SET_ACTIVE_SUBITEM = `${namespace}_SET_ACTIVE_SUBITEM`;
export const SET_TOKENS = `${namespace}_SET_TOKENS`;
export const SET_USER_PRIVILEGES = `${namespace}_SET_USER_PRIVILEGES`;
export const SET_LEARNING_LINK = `${namespace}_SET_LEARNING_LINK`;
export const SET_ATTEMPTED_ROUTE = `${namespace}_SET_ATTEMPTED_ROUTE`;
export const SET_RETAILER_GROUPS = `${namespace}_SET_RETAILER_GROUPS`;
export const SET_OWNER_RETAILER_GROUPS = `${namespace}_SET_OWNER_RETAILER_GROUPS`;
export const TOGGLE_SUBMENU = 'TOGGLE_SUBMENU';
export const CLOSE_ALL_SIDEMENU_ITEMS = 'CLOSE_ALL_SIDEMENU_ITEMS';

export const saveSession = () => (dispatch, getState) => {
  const { session } = getState();
  DepsService.$sessionStorage.userSession = _.cloneDeep(session);
  return Promise.resolve();
};

export const initializeSession = (session) => (dispatch) => {
  dispatch({
    type: INIT_SESSION,
    payload: session,
  });
  dispatch(saveSession());
};

export const applySessionData = (session) => (dispatch) => {
  dispatch({
    type: UPDATE_SESSION,
    payload: session,
  });
  dispatch(saveSession());
};

export const applyTopMenu = (topMenuItems) => (dispatch) => {
  dispatch({
    type: SET_TOP_MENU,
    payload: topMenuItems,
  });
  dispatch(saveSession());
};

const subLevelItems = (items, id) => items.filter((x) => x.id && x.id.indexOf(`${id}.`) !== -1);

const hasAtLeastOneSubItemVisible = (items, item) => {
  const subItems = subLevelItems(items, item.id);
  return subItems.length > 0 && subItems.filter((subItem) => (!subItem.plainLink && subItem.hasAccess) || subItem.plainLink).length > 0;
};

export const applySideMenu = (sideMenuItems) => (dispatch) => {
  const mainItems = sideMenuItems ? sideMenuItems.filter((x) => !x.id || x.id.indexOf('.') === -1) : [];

  mainItems.forEach((item) => {
    item.subItems = subLevelItems(sideMenuItems, item.id);
    item.subItems = item.subItems.filter((x) => x.plainLink || x.hasAccess || hasAtLeastOneSubItemVisible(sideMenuItems, x));
  });

  dispatch({
    type: SET_SIDE_MENU,
    payload: mainItems,
  });

  dispatch(saveSession());
};

export const applyMenu =
  (newModel = {}) =>
  (dispatch, getState) => {
    const {
      session,
      session: {
        role,
        retailer: { retailerType, isSuperChain },
      },
    } = getState();
    const roleConfig = DepsService.config.roles[role] || {};
    let roleKey;

    if (retailerType === RETAILER_TYPE.CHAIN_HEAD && isSuperChain) {
      roleKey = `${role}_${RETAILER_TYPE.CHAIN_HEAD}_${RETAILER_TYPE.SUPER_CHAIN}`;
    } else {
      roleKey = retailerType ? `${role}_${retailerType}` : role;
    }

    const sidemenu = helpers.generateMenu(_.get(menu, `roles[${roleKey}].sidemenu`) || roleConfig.sidemenu, {
      training: {
        href: session.lotteryLearningLink,
      },
      branding: {
        href: DepsService.config.brandingUrl,
        label: DepsService.config.brandingLabel,
      },
    });

    const topmenu = helpers.generateTopMenu(_.get(DepsService.config, `roles[${roleKey}].topmenu`) || roleConfig.topmenu);

    dispatch(applyTopMenu(topmenu));
    dispatch(applySideMenu(sidemenu));

    return newModel;
  };

export const toggleSubmenu = ({ id }) => ({
  type: TOGGLE_SUBMENU,
  id,
});

export const closeAllSideMenuItems = () => (dispatch, getState) => {
  const { session } = getState();
  const { sidemenu } = session;

  const opened = sidemenu.find((item) => item.opened === true);

  if (opened) return dispatch({ type: CLOSE_ALL_SIDEMENU_ITEMS });
};

export const setCurrentState = (toState, fromState) => (dispatch, getState) => {
  const {
    session: { sidemenu },
  } = getState();

  const reducedItems = sidemenu.reduce((reduced, reducedItem) => {
    reducedItem.active = false;

    if (toState.startsWith(reducedItem.sref)) {
      reducedItem.active = true;
      reducedItem.opened = fromState ? reducedItem.opened : !reducedItem.opened;
    } else {
      reducedItem.opened = fromState ? reducedItem.opened : false;
    }

    if (reducedItem.subItems) {
      reducedItem.subItems.forEach((subItem) => {
        subItem.active = false;
        if (toState.startsWith(subItem.sref)) {
          reducedItem.active = true;
          reducedItem.opened = fromState ? reducedItem.opened : true;
          subItem.active = true;
        }
      });
    }

    reduced.push(reducedItem);
    return reduced;
  }, []);

  dispatch({
    type: SET_SIDE_MENU,
    payload: reducedItems,
  });
};

export const changePageTitle = (pageTitle) => (dispatch) => {
  dispatch({
    type: SET_PAGE_TITLE,
    payload: { pageTitle },
  });
};

export const changeLocale = (locale) => (dispatch) => {
  moment.locale(locale);
  DepsService.gettextCatalog.setCurrentLanguage(locale);
  DepsService.gettextCatalog.loadRemote(`./languages/${locale}.json`);

  dispatch({
    type: SET_LOCALE,
    payload: { locale },
  });
  dispatch(saveSession());
};

export const setRetailerDashboardType = (retailerDashboardType, sessionId) => (dispatch) => {
  const isChainHead = retailerDashboardType === RETAILER_TYPE.CHAIN_HEAD;
  const isSuperChain = retailerDashboardType === RETAILER_TYPE.SUPER_CHAIN;
  const isIndependent = retailerDashboardType === RETAILER_TYPE.INDEPENDENT;
  const isChainSubordinate = retailerDashboardType === RETAILER_TYPE.CHAIN_SUBORDINATE;

  dispatch({
    type: UPDATE_SESSION,
    payload: {
      retailerDashboard: {
        isChainHead,
        isSuperChain,
        isIndependent,
        isChainSubordinate,
        sessionId,
      },
    },
  });
  return dispatch(saveSession());
};

export const cleartRetailerDashboardType = () => (dispatch) => {
  dispatch({
    type: UPDATE_SESSION,
    payload: {
      retailerDashboard: {
        isChainHead: false,
        isSuperChain: false,
        isIndependent: false,
        isChainSubordinate: false,
      },
    },
  });
  return dispatch(saveSession());
};

export const resetSession = () => () => {
  initializeSession({ ...DepsService.anonymousPrivileges });
};

export const updateAuthHeaders = () => (dispatch, getState) => {
  const {
    session: {
      tokens: {
        jwt: { code },
      },
    },
  } = getState();
  DepsService.restSrv.HttpService.defaults.headers.common.Authorization = code ? `Bearer ${code}` : null;
};

export const applyTokens = (tokens) => (dispatch) => {
  if (!tokens) {
    return Promise.reject();
  }

  const currentTimeStamp = Date.now();
  const jwtTokenExpiration = currentTimeStamp + (_.get(tokens, '0.tokenParams.expiresIn') || 0) * 1000;
  const refreshTokenExpiration = currentTimeStamp + (_.get(tokens, '1.tokenParams.expiresIn') || 0) * 1000;
  const jwtCode = tokens[0].token;
  const refreshCode = tokens[1].token;

  const sessionTokens = {
    jwt: {
      code: jwtCode,
      expiringTimestamp: jwtTokenExpiration,
      passwordExpired: tokens[0].tokenParams.passwordExpired,
    },
    refresh: {
      code: refreshCode,
      expiringTimestamp: refreshTokenExpiration,
    },
  };

  dispatch({
    type: SET_TOKENS,
    payload: sessionTokens,
  });

  dispatch(updateAuthHeaders());
  dispatch(saveSession());
};

export const setUserPrivileges = (privileges) => (dispatch) => {
  dispatch({
    type: SET_USER_PRIVILEGES,
    payload: privileges,
  });
  dispatch(saveSession());
};

export const setGtmsTime = (gtmsTimeUnix) => (dispatch, getState) => {
  const { session } = getState();
  const newSession = { ...session, gtmsTime: gtmsTimeUnix };

  dispatch({
    type: UPDATE_SESSION,
    payload: newSession,
  });
  dispatch(saveSession());
};

const timezoneDiffToSeconds = (timezoneDiff) => ((timezoneDiff * 1) / 100) * 3600;

const calculateLotteryTime = (serverTime, serverTimezoneDiff, lotteryTimezoneDiff) => {
  const serverTimezoneDiffSeconds = timezoneDiffToSeconds(serverTimezoneDiff);
  const lotteryTimezoneDiffSeconds = timezoneDiffToSeconds(lotteryTimezoneDiff);
  const serverTimeInUTC = serverTime - serverTimezoneDiffSeconds;
  const lotteryTimeWithTimezone = serverTimeInUTC + lotteryTimezoneDiffSeconds;
  return lotteryTimeWithTimezone;
};

export const setServerTime = (serverTime, serverTimezoneDiff, lotteryTimezoneDiff, salesPeriodTime) => (dispatch, getState) => {
  const { session } = getState();
  const serverTimestamp = moment.utc(serverTime).unix();
  const salesPeriodTimestamp = moment.utc(salesPeriodTime).unix();

  const newSession = {
    ...session,
    serverTime: serverTimestamp,
    serverTimezoneDiff,
    salesPeriodTimestamp,
    lotteryTime: calculateLotteryTime(serverTimestamp, serverTimezoneDiff, lotteryTimezoneDiff),
    lotteryTimezoneDiff,
  };

  dispatch({
    type: UPDATE_SESSION,
    payload: newSession,
  });
  dispatch(saveSession());
};

export const setSuperChainRetailer = (newRetailer) => (dispatch, getState) => {
  const {
    session: { retailer },
  } = getState();

  const newRetailerObj = {
    ...retailer,
    retailerId: newRetailer.retailerId,
    retailerType: newRetailer.retailerType,
    isChain: newRetailer.retailerType ? newRetailer.retailerType.indexOf(CHAIN_SUFFIX) !== -1 : false,
    isChainHead: newRetailer.retailerType ? newRetailer.retailerType === RETAILER_TYPE.CHAIN_HEAD : false,
    isChainSubordinate: newRetailer.retailerType ? newRetailer.retailerType === RETAILER_TYPE.CHAIN_SUBORDINATE : false,
  };

  const newSession = {
    retailer: newRetailerObj,
  };

  dispatch({
    type: UPDATE_SESSION,
    payload: newSession,
  });
  dispatch(saveSession());
};

export const setStoreProfileRetailer = (isStoreProfile) => (dispatch, getState) => {
  const {
    session: { retailer },
  } = getState();

  const newSession = {
    retailer: { ...retailer, isStoreProfile },
  };

  dispatch({
    type: UPDATE_SESSION,
    payload: newSession,
  });
  dispatch(saveSession());
};

export const setRetailerGroups = (retailerGroups) => (dispatch) => {
  dispatch({
    type: SET_RETAILER_GROUPS,
    payload: retailerGroups,
  });
  dispatch(saveSession());
};

export const setOwnerRetailerGroups = (retailerGroups) => (dispatch) => {
  dispatch({
    type: SET_OWNER_RETAILER_GROUPS,
    payload: retailerGroups,
  });
  dispatch(saveSession());
};

export const setLearningLink = (link) => ({
  type: SET_LEARNING_LINK,
  payload: link,
});

export const setAttemptedRoute = (route) => ({
  type: SET_ATTEMPTED_ROUTE,
  payload: route,
});



// WEBPACK FOOTER //
// ./src/modules/auth/actions/session.actions.js